import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import SwiperCore, { Navigation, Pagination, Swiper, Thumbs, A11y, Keyboard } from "swiper";

SwiperCore.use([Thumbs, Navigation, Pagination, Swiper, A11y, Keyboard]);

const SLIDER_A11Y = {
    enabled: true,
    prevSlideMessage: 'Poprzednie wideo',
    nextSlideMessage: 'Następne wideo',
    paginationBulletMessage: 'Przejdź do wideo {{index}}',
};

interface VideoSlide {
    videoSrc: string;
    poster?: string;
    thumbSrc?: string;
    thumbAlt?: string;
    title?: string;
    captionsSrc?: string;
    captionsSrcEn?: string;
}

@Component({
    selector: "app-video-gallery-carousel",
    templateUrl: "./video-gallery-carousel.component.html",
    styleUrls: ["./video-gallery-carousel.component.scss"],
})
export class VideoGalleryCarouselComponent implements AfterViewInit, OnDestroy {
    @Input() sectionId = "";
    @Input() title = "";
    @Input() titleHtml = "";
    @Input() withOverlayBackground = false;
    @Input() videos: VideoSlide[] = [
        {
            videoSrc: "./assets/images/vid/30_rps_nauczyciel_final_2026.mp4",
            poster: "./assets/images/video-placeholder-desktop.jpg",
            thumbSrc: "./assets/images/obbi_slide-1-thumb.jpg",
            thumbAlt: "Wideo miniatura 1",
        },
        {
            videoSrc: "./assets/images/vid/30_rps_nauczyciel_final_2026.mp4",
            poster: "./assets/images/video-placeholder-desktop.jpg",
            thumbSrc: "./assets/images/obbi_slide-2-thumb.jpg",
            thumbAlt: "Wideo miniatura 2",
        },
        {
            videoSrc: "./assets/images/vid/30_rps_nauczyciel_final_2026.mp4",
            poster: "./assets/images/video-placeholder-desktop.jpg",
            thumbSrc: "./assets/images/obbi_slide-3-thumb.jpg",
            thumbAlt: "Wideo miniatura 3",
        },
    ];

    private galleryMain?: Swiper;
    private galleryThumbs?: Swiper;
    private screenWidth = window.innerWidth;
    activeSlideIndex = 0;
    modalVideoSrc: string | null = null;
    modalAriaLabel: string | null = null;
    modalCaptionsSrc: string | null = null;
    modalCaptionsSrcEn: string | null = null;
    private readonly maxAutoplaySeconds = 25;
    private lastFocusedElement: HTMLElement | null = null;
    @ViewChild("modalRoot")
    modalRoot?: ElementRef<HTMLElement>;

    constructor(private hostRef: ElementRef<HTMLElement>) {}

    get plainTitle(): string {
        const raw = this.titleHtml || this.title || "";
        return raw
            .replace(/<[^>]*>/g, " ")
            .replace(/\s+/g, " ")
            .replace(/[:\s]+$/, "")
            .trim();
    }

    videoAriaLabel(videoItem: VideoSlide, index: number): string {
        const base = this.plainTitle;
        const name = videoItem.title?.trim();
        if (base && name) {
            return `${base} – ${name}`;
        }
        if (base) {
            return `${base} – wideo ${index + 1}`;
        }
        if (name) {
            return name;
        }
        return `Wideo ${index + 1}`;
    }

    @HostListener("window:resize", ["$event"])
    onResize(event: Event) {
        this.screenWidth = (event.target as Window).innerWidth;
        this.rebuild();
    }

    ngAfterViewInit() {
        this.initSwipers();
        this.attachAutoplayLimitHandlers();
    }

    ngOnDestroy() {
        this.destroySwipers();
    }

    openVideoModal(videoItem: VideoSlide, ariaLabel: string | null = null) {
        this.lastFocusedElement = document.activeElement as HTMLElement;
        this.modalVideoSrc = videoItem.videoSrc;
        this.modalAriaLabel = ariaLabel;
        this.modalCaptionsSrc = videoItem.captionsSrc || null;
        this.modalCaptionsSrcEn = videoItem.captionsSrcEn || null;
        this.pauseAllVideosExcept();
    }

    closeVideoModal() {
        this.modalVideoSrc = null;
        this.modalAriaLabel = null;
        this.modalCaptionsSrc = null;
        this.modalCaptionsSrcEn = null;
        this.lastFocusedElement?.focus();
        this.lastFocusedElement = null;
    }

    @HostListener("document:keydown.escape")
    onEscapeKey() {
        if (this.modalVideoSrc) {
            this.closeVideoModal();
        }
    }

    @HostListener("document:focusin", ["$event"])
    onDocumentFocusIn(event: FocusEvent) {
        if (!this.modalVideoSrc) {
            return;
        }

        const modal = this.modalRoot?.nativeElement;
        const target = event.target as HTMLElement | null;
        if (!modal || !target || this.isFocusWithinModal(modal, target)) {
            return;
        }

        modal.querySelector<HTMLElement>(".video-modal__close")?.focus();
    }

    private isFocusWithinModal(modal: HTMLElement, target: HTMLElement): boolean {
        if (modal.contains(target)) {
            return true;
        }

        const video = modal.querySelector("video");
        return !!video?.shadowRoot?.contains(target);
    }

    onThumbClick(index: number) {
        if (!this.galleryMain) return;

        this.galleryMain.slideTo(index);

        setTimeout(() => {
            this.playActiveVideoMuted();
        }, 50);
    }

    private rebuild() {
        this.destroySwipers();
        this.initSwipers();
        this.attachAutoplayLimitHandlers();
    }

    private destroySwipers() {
        if (this.galleryMain) {
            this.galleryMain.destroy(true, true);
            this.galleryMain = undefined;
        }
        if (this.galleryThumbs) {
            this.galleryThumbs.destroy(true, true);
            this.galleryThumbs = undefined;
        }
    }

    private initSwipers() {
        const host = this.hostRef.nativeElement;
        const mainContainer = host.querySelector(
            ".gallery-main",
        ) as HTMLElement;
        const thumbsContainer = host.querySelector(
            ".gallery-thumbs",
        ) as HTMLElement;
        const nextEl = host.querySelector(".swiper-button-next") as HTMLElement;
        const prevEl = host.querySelector(".swiper-button-prev") as HTMLElement;
        const paginationEl = host.querySelector(
            ".swiper-pagination",
        ) as HTMLElement;
        const isDesktop = this.screenWidth >= 992;

        if (isDesktop && thumbsContainer) {
            this.galleryThumbs = new Swiper(thumbsContainer, {
                slidesPerView: 3,
                direction: "vertical",
                spaceBetween: 0,
                watchSlidesVisibility: true,
                watchSlidesProgress: true,
                allowTouchMove: false,
                slideToClickedSlide: false,
            });
        }

        if (isDesktop) {
            this.galleryMain = new Swiper(mainContainer, {
                slidesPerView: 1,
                spaceBetween: 24,
                navigation: { nextEl, prevEl },
                pagination: isDesktop
                    ? false
                    : { el: paginationEl, clickable: true },
                thumbs: {
                    swiper: this.galleryThumbs || null,
                },
                slidesOffsetAfter: 0,
                slidesOffsetBefore: 0,
                a11y: SLIDER_A11Y,
                keyboard: { enabled: true },
            });
        } else {
            this.galleryMain = new Swiper(mainContainer, {
                slidesPerView: 1.2,
                spaceBetween: 24,
                navigation: { nextEl, prevEl },
                pagination: isDesktop
                    ? false
                    : { el: paginationEl, clickable: true },
                thumbs: {
                    swiper: this.galleryThumbs || null,
                },
                slidesOffsetAfter: 24,
                slidesOffsetBefore: 24,
                a11y: SLIDER_A11Y,
                keyboard: { enabled: true },
            });
        }

        this.galleryMain.on("slideChange", () => {
            this.activeSlideIndex = this.galleryMain?.activeIndex || 0;
            this.playActiveVideoMuted();
        });

        this.galleryMain.on("slideChange", () => {
            this.activeSlideIndex = this.galleryMain?.activeIndex || 0;
        });

        this.galleryMain.on("transitionEnd", () => {
            this.playActiveVideoMuted();
        });

        this.galleryMain.on("slideChangeTransitionStart", () => {
            this.pauseAllVideosExcept();
        });

        this.activeSlideIndex = this.galleryMain.activeIndex || 0;
        this.playActiveVideoMuted();
        this.syncThumbHeight();
    }

    private syncThumbHeight() {
        if (this.screenWidth < 992) {
            return;
        }

        const host = this.hostRef.nativeElement;
        const mainVideo = host.querySelector(
            ".gallery-main video",
        ) as HTMLVideoElement;
        const thumbs = host.querySelector(".gallery-thumbs") as HTMLElement;

        if (!mainVideo || !thumbs) {
            return;
        }

        const applyHeight = () => {
            const mainHeight = mainVideo.getBoundingClientRect().height;
            if (mainHeight > 0) {
                thumbs.style.height = `${mainHeight}px`;
            }
        };

        applyHeight();
        mainVideo.onloadedmetadata = applyHeight;
    }

    private pauseAllVideosExcept(activeVideo?: HTMLVideoElement) {
        const allVideos = this.hostRef.nativeElement.querySelectorAll("video");
        allVideos.forEach((videoEl: HTMLVideoElement) => {
            if (activeVideo && videoEl === activeVideo) {
                return;
            }
            videoEl.pause();
        });
    }

    private playActiveVideoMuted() {
        const activeVideo = this.hostRef.nativeElement.querySelector(
            ".gallery-main .swiper-slide-active video",
        ) as HTMLVideoElement;
        if (!activeVideo) {
            return;
        }

        this.pauseAllVideosExcept(activeVideo);
        activeVideo.muted = true;
        activeVideo.currentTime = 0;
        // Debug (optional): uncomment during local QA to inspect autoplay behavior.
        // console.debug('[video-gallery] autoplay start', { index: this.activeSlideIndex });
        void activeVideo.play().catch(() => undefined);
    }

    private attachAutoplayLimitHandlers() {
        const videos = this.hostRef.nativeElement.querySelectorAll(
            ".gallery-main video",
        );
        videos.forEach((videoEl: HTMLVideoElement) => {
            videoEl.ontimeupdate = () => {
                const duration =
                    Number.isFinite(videoEl.duration) && videoEl.duration > 0
                        ? videoEl.duration
                        : this.maxAutoplaySeconds;
                const stopAt = Math.min(this.maxAutoplaySeconds, duration);
                if (videoEl.currentTime >= stopAt) {
                    // Debug (optional): uncomment during local QA to verify 25s cutoff.
                    // console.debug('[video-gallery] autoplay stop', { currentTime: videoEl.currentTime, stopAt });
                    videoEl.pause();
                }
            };
        });
    }
}
